import { useEffect, useRef, useState } from 'react'
import { t } from './sprache'
import { Periodenfeld } from './Periodenfeld'
import { ariaSortierung, naechste } from './Sortierung'
import type { Sortierwahl } from './Sortierung'
import { ausPeriodenfeld } from './Zeitraum'

/**
 * Filtern am Spaltenkopf — das Gegenstück zum Sortieren (7.6 Abschnitt 4,
 * R650).
 *
 * **Ein Bauteil und keine zweite Abschrift.** Dieselbe Entscheidung wie bei
 * der Stammdatenpflege (R317): Es gibt *ein* Bauteil für den Spaltenkopf, und
 * die Arbeitsbereiche betten es ein. Zwei Tabellen, die sich beim Filtern
 * verschieden verhalten, sind schlimmer als eine, die gar nicht filtert —
 * genau so steht es schon über dem Sortieren (`Sortierung.ts`).
 *
 * **Der Kopf trägt beides, und sie sind auseinanderzuhalten:**
 *
 * | Griff | Was er tut |
 * |---|---|
 * | Klick auf die **Beschriftung** | sortiert — auf, ab, zurück zur Ordnung der Liste |
 * | Klick auf den **Trichter** | öffnet den Filter dieser Spalte |
 *
 * Beides auf dieselbe Fläche zu legen wäre der Fehler: Wer sortieren will,
 * bekäme ein Fenster, und wer filtern will, verschöbe die Zeilen. Der Trichter
 * steht deshalb als eigener Knopf daneben — und er ist **gefüllt, wenn der
 * Filter wirkt**, nicht nur farbig (R143): Ein Anwender, der seinen Bildschirm
 * in Graustufen ausdruckt, muss sehen, dass er eine gefilterte Liste vor sich
 * hat.
 *
 * **Drei Arten, mehr nicht** (R146 — fester Vorrat, freie Ausprägung):
 *
 * - `text` — enthält. Für Bezeichnungen, Nachweise, Anwendernamen.
 * - `auswahl` — Häkchen aus einem festen Vorrat. Für Herkunft, Befund, Art.
 * - `bereich` — von/bis. Für Zahlen; für Perioden als `periodenbereich`.
 *
 * Eine vierte Art wäre eine neue Bedeutung und kein Konfigurationsfall.
 *
 * **Der Periodenbereich ist keine vierte Bedeutung** (R839 — Eine Periode
 * liest sich als Monat und Jahr; Entscheidung vom 2026-09-30). Er ist der
 * Bereich mit der Eingabe, die an jeder anderen Stelle für eine Periode gilt:
 * dasselbe Periodenfeld, dieselben Schreibweisen, derselbe Hinweis. Bis dahin
 * verglich der Filter der Periodenspalte per `Number(...)` und verlangte
 * damit `202701` — die Stelle, an der die Speicherform noch getippt werden
 * musste. Er steht als eigene Angabe da und nicht als Schalter an `bereich`,
 * weil `trifft` die Art bekommt und nicht die Spalte; die Kursspalten
 * daneben bleiben Zahlen.
 */

export type Filterart = 'text' | 'auswahl' | 'bereich' | 'periodenbereich'

/** Was an einer Spalte gefiltert ist. */
export type Filterwert = {
  text?: string
  /** Die abgehakten Werte. Leer heißt „alle" — nicht „keine". */
  auswahl?: string[]
  von?: string
  bis?: string
}

/** Der Filterstand der ganzen Tabelle, je Spalte einer. */
export type Filterstand = Record<string, Filterwert>

/** Ein Wert, wie ihn eine Zelle zum Filtern hergibt. */
export type Filterzelle = string | number | null

/**
 * Ob an dieser Spalte überhaupt etwas gefiltert ist.
 *
 * **Eine leere Auswahl heißt „alle".** Sie als „keine" zu lesen, wäre die
 * Falle, in die jede Filterleiste einmal tappt: Wer das letzte Häkchen
 * wegnimmt, bekäme eine leere Tabelle und hielte sie für einen Fehler.
 */
export function wirkt(wert: Filterwert | undefined): boolean {
  if (!wert) {
    return false
  }

  return (
    (wert.text ?? '').trim().length > 0 ||
    (wert.auswahl?.length ?? 0) > 0 ||
    (wert.von ?? '').trim().length > 0 ||
    (wert.bis ?? '').trim().length > 0
  )
}

/** Wie viele Spalten gerade filtern. */
export function anzahlFilter(stand: Filterstand): number {
  return Object.values(stand).filter(wirkt).length
}

/**
 * Trifft eine Zelle den Filter ihrer Spalte?
 *
 * **Leer wird vom Bereichsfilter ausgeschlossen, vom Textfilter nicht.** Wer
 * „Perioden ab Januar 2027" sagt, meint Zeilen mit einer Periode; wer „enthält EZB"
 * sagt, meint eine Zeichenkette, und eine leere Zelle enthält sie eben nicht.
 * Beides läuft auf dasselbe hinaus — es steht hier trotzdem ausdrücklich, weil
 * es beim nächsten Lesen sonst wie ein Versehen aussieht.
 */
export function trifft(zelle: Filterzelle, wert: Filterwert | undefined, art: Filterart): boolean {
  if (!wirkt(wert)) {
    return true
  }

  const filter = wert!

  if (art === 'text') {
    const gesucht = (filter.text ?? '').trim().toLowerCase()
    return gesucht.length === 0 || String(zelle ?? '').toLowerCase().includes(gesucht)
  }

  if (art === 'auswahl') {
    const gewaehlt = filter.auswahl ?? []
    if (gewaehlt.length === 0) {
      return true
    }

    /*
      **Eine Zelle darf mehrere Ausprägungen tragen** — getrennt durch einen
      senkrechten Strich. Die Zeile einer Kurstabelle hat bis zu drei Kurse mit
      je eigener Herkunft; „zeige mir die fortgeschriebenen" meint dann die
      Zeilen, in denen *einer* fortgeschrieben ist, und nicht die, in denen
      alle drei es sind. Eine Filterspalte, die sich für eine der drei
      entscheiden müsste, gäbe die falsche Auskunft.
    */
    return String(zelle ?? '')
      .split('|')
      .some((teil) => gewaehlt.includes(teil))
  }

  const zahl = typeof zelle === 'number' ? zelle : Number(zelle)
  if (zelle === null || Number.isNaN(zahl)) {
    return false
  }

  const von = (filter.von ?? '').trim()
  const bis = (filter.bis ?? '').trim()

  /*
    **Eine Periodengrenze wird gelesen wie im Periodenfeld** (R839): `01.2027`,
    `01/2027`, `2027-01` und `202701` sind derselbe Januar. Die Zelle trägt
    die Periode weiter als Zahl — verglichen wird also wie im Bereich, nur die
    Grenze kommt aus der Lesung.

    **Eine Grenze, die sich nicht lesen lässt, grenzt nicht ein.** Sie als
    „nichts trifft" zu werten, leerte die Tabelle wegen eines Tippfehlers.
  */
  if (art === 'periodenbereich') {
    const ab = ausPeriodenfeld(von)
    const hoechstens = ausPeriodenfeld(bis)

    if (ab !== null && zahl < ab) {
      return false
    }

    return !(hoechstens !== null && zahl > hoechstens)
  }

  if (von.length > 0 && zahl < Number(von)) {
    return false
  }

  return !(bis.length > 0 && zahl > Number(bis))
}

/** Beschreibt eine Spalte — Beschriftung, Filterart und ihr Wertevorrat. */
export type Spaltenbeschreibung = {
  id: string
  kopf: string
  /** Rechtsbündig mit tabellarischen Ziffern. */
  zahl?: boolean
  /** Ohne Angabe: Die Spalte lässt sich sortieren, aber nicht filtern. */
  filter?: Filterart
  /** Bei `auswahl`: der Vorrat, als Wert und Beschriftung. */
  vorrat?: { wert: string; text: string }[]
}

/**
 * Ein Spaltenkopf, der sortiert **und** filtert.
 *
 * Er ersetzt `Spaltenkopf` aus `Journalsuche.tsx` nicht, sondern erweitert
 * ihn: Wo nur sortiert wird, bleibt der schlanke Kopf richtig — ein Trichter
 * an einer Spalte, die nichts zu filtern hat, wäre ein Knopf, der nichts tut.
 */
export function Filterkopf({
  spalte,
  wahl,
  aufSortierung,
  filter,
  aufFilter,
}: {
  spalte: Spaltenbeschreibung
  wahl: Sortierwahl
  aufSortierung: () => void
  filter: Filterwert | undefined
  aufFilter: (wert: Filterwert) => void
}) {
  const [offen, setOffen] = useState(false)
  const rahmen = useRef<HTMLDivElement | null>(null)
  const aktivSortiert = wahl?.spalte === spalte.id
  const aktivGefiltert = wirkt(filter)

  // **Ein Klick daneben schließt das Fenster, die Esc-Taste auch.** Ein
  // Filterfenster, das offen stehen bleibt, verdeckt die Zeilen, für die man
  // es geöffnet hat.
  useEffect(() => {
    if (!offen) {
      return
    }

    function daneben(e: MouseEvent) {
      if (rahmen.current && !rahmen.current.contains(e.target as Node)) {
        setOffen(false)
      }
    }

    function taste(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOffen(false)
      }
    }

    document.addEventListener('mousedown', daneben)
    document.addEventListener('keydown', taste)
    return () => {
      document.removeEventListener('mousedown', daneben)
      document.removeEventListener('keydown', taste)
    }
  }, [offen])

  return (
    <div className="kopfzelle" ref={rahmen}>
      <button
        type="button"
        className="spaltenkopf"
        title={t('sortierung.hilfe')}
        onClick={aufSortierung}
      >
        {spalte.kopf}
        <span className={`pfeil${aktivSortiert ? ' aktiv' : ''}`} aria-hidden="true">
          {aktivSortiert ? (wahl!.richtung === 'auf' ? '▲' : '▼') : '▾'}
        </span>
      </button>

      {spalte.filter && (
        /*
          **Hier ist aria-expanded die richtige Marke, nicht aria-pressed**
          (Befund der Bauweisenprüfung vom 2026-09-29). Der Trichter schaltet
          nichts um, er klappt ein Menü auf — ein aria-pressed behauptete einen
          gedrückten Schalter, den es nicht gibt. Daß gefiltert wird, steht
          nicht in der Farbe allein: Es steht im Namen der Schaltfläche, den
          die Sprachausgabe vorliest (R143).

          Die Ausnahme steht in der letzten Zeile vor dem Fundort — weiter als
          vier Zeilen reicht die Prüfung nicht zurück (R470).

          bauweise-ausnahme B2: aria-expanded ist die Marke dieses Knopfes, der Filterzustand steht in seinem Namen
        */
        <button
          type="button"
          className={`trichter${aktivGefiltert ? ' aktiv' : ''}`}
          aria-expanded={offen}
          aria-label={t(aktivGefiltert ? 'filter.aktiv.hilfe' : 'filter.hilfe', { spalte: spalte.kopf })}
          title={t(aktivGefiltert ? 'filter.aktiv.hilfe' : 'filter.hilfe', { spalte: spalte.kopf })}
          onClick={() => setOffen((o) => !o)}
        >
          {/* Gefüllt, wenn der Filter wirkt — nicht nur farbig (R143). */}
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
            <path
              d="M1.5 2.5h13l-5 6v5l-3 1.5v-6.5z"
              fill={aktivGefiltert ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {offen && spalte.filter && (
        <div className="filterfenster">
          <Filterfeld
            art={spalte.filter}
            vorrat={spalte.vorrat ?? []}
            wert={filter ?? {}}
            aufWert={aufFilter}
          />
          <div className="filterfuss">
            <button
              type="button"
              className="schmal"
              onClick={() => {
                aufFilter({})
                setOffen(false)
              }}
            >
              {t('filter.loesen')}
            </button>
            <button type="button" onClick={() => setOffen(false)}>
              {t('filter.fertig')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Filterfeld({
  art,
  vorrat,
  wert,
  aufWert,
}: {
  art: Filterart
  vorrat: { wert: string; text: string }[]
  wert: Filterwert
  aufWert: (wert: Filterwert) => void
}) {
  if (art === 'text') {
    return (
      <label className="filterzeile">
        <span>{t('filter.enthaelt')}</span>
        <input
          type="search"
          autoFocus
          value={wert.text ?? ''}
          onChange={(e) => aufWert({ ...wert, text: e.target.value })}
        />
      </label>
    )
  }

  if (art === 'periodenbereich') {
    return <Periodenbereich wert={wert} aufWert={aufWert} />
  }

  if (art === 'bereich') {
    return (
      <>
        <label className="filterzeile">
          <span>{t('filter.von')}</span>
          <input
            type="text"
            inputMode="numeric"
            autoFocus
            value={wert.von ?? ''}
            onChange={(e) => aufWert({ ...wert, von: e.target.value })}
          />
        </label>
        <label className="filterzeile">
          <span>{t('filter.bis')}</span>
          <input
            type="text"
            inputMode="numeric"
            value={wert.bis ?? ''}
            onChange={(e) => aufWert({ ...wert, bis: e.target.value })}
          />
        </label>
      </>
    )
  }

  const gewaehlt = wert.auswahl ?? []

  return (
    <ul className="filterauswahl">
      {vorrat.map((eintrag) => (
        <li key={eintrag.wert}>
          <label>
            <input
              type="checkbox"
              checked={gewaehlt.includes(eintrag.wert)}
              onChange={(e) =>
                aufWert({
                  ...wert,
                  auswahl: e.target.checked
                    ? [...gewaehlt, eintrag.wert]
                    : gewaehlt.filter((g) => g !== eintrag.wert),
                })
              }
            />
            {eintrag.text}
          </label>
        </li>
      ))}
    </ul>
  )
}

/**
 * Von/bis für eine Periodenspalte — zweimal das Periodenfeld (R839 — Eine
 * Periode liest sich als Monat und Jahr; R502 — Am Bildschirm steht MM.JJJJ).
 *
 * **Im Filterstand steht die Periode, nicht der getippte Text.** Das Feld
 * meldet eine Periode oder keine; solange keine dasteht, ist die Grenze leer
 * und der Trichter bleibt hohl. Ein halb getipptes „01.20" filtert damit
 * nicht — und behauptet es auch nicht.
 */
function Periodenbereich({
  wert,
  aufWert,
}: {
  wert: Filterwert
  aufWert: (wert: Filterwert) => void
}) {
  const erstes = useRef<HTMLInputElement | null>(null)

  // Das Fenster geht auf, und man tippt — wie bei den anderen Arten.
  useEffect(() => {
    erstes.current?.focus()
  }, [])

  const grenze = (periode: number | null) => (periode === null ? '' : String(periode))

  return (
    <>
      <Periodenfeld
        klasse="filterzeile"
        beschriftung={t('filter.von')}
        wert={ausPeriodenfeld(wert.von ?? '')}
        aufWert={(periode) => aufWert({ ...wert, von: grenze(periode) })}
        erstesFeld={erstes}
      />
      <Periodenfeld
        klasse="filterzeile"
        beschriftung={t('filter.bis')}
        wert={ausPeriodenfeld(wert.bis ?? '')}
        aufWert={(periode) => aufWert({ ...wert, bis: grenze(periode) })}
      />
    </>
  )
}

/**
 * Die Kopfzeile einer Tabelle, die sortiert und filtert.
 *
 * **Ein Bauteil für die ganze Zeile und nicht je Spalte eines**: Wer eine
 * Spalte hinzufügt, ergänzt eine Zeile in seiner Spaltenliste — und nicht ein
 * `th` samt Sortier- und Filterverdrahtung, die beim dritten Mal von der
 * zweiten abweicht.
 */
export function Filterkopfzeile({
  spalten,
  wahl,
  aufWahl,
  stand,
  aufStand,
}: {
  spalten: Spaltenbeschreibung[]
  wahl: Sortierwahl
  aufWahl: (wahl: Sortierwahl) => void
  stand: Filterstand
  aufStand: (stand: Filterstand) => void
}) {
  return (
    <tr>
      {spalten.map((s) => (
        <th key={s.id} className={s.zahl ? 'zahl' : undefined} aria-sort={ariaSortierung(wahl, s.id)}>
          <Filterkopf
            spalte={s}
            wahl={wahl}
            aufSortierung={() => aufWahl(naechste(wahl, s.id))}
            filter={stand[s.id]}
            aufFilter={(wert) => aufStand({ ...stand, [s.id]: wert })}
          />
        </th>
      ))}
    </tr>
  )
}

/**
 * Wendet alle Spaltenfilter auf eine Liste an.
 *
 * **Und-Verknüpfung über die Spalten hinweg**: Zwei Filter grenzen ein, sie
 * erweitern nicht. Alles andere wäre bei einer Tabelle, in der man etwas
 * sucht, kaum je gemeint.
 */
export function gefiltert<T>(
  zeilen: readonly T[],
  spalten: Spaltenbeschreibung[],
  stand: Filterstand,
  zelle: (zeile: T, spalte: string) => Filterzelle,
): T[] {
  const wirksam = spalten.filter((s) => s.filter && wirkt(stand[s.id]))
  if (wirksam.length === 0) {
    return [...zeilen]
  }

  return zeilen.filter((zeile) =>
    wirksam.every((s) => trifft(zelle(zeile, s.id), stand[s.id], s.filter!)),
  )
}
